import { inject } from '@angular/core';
import { ApplicationInsights } from '@microsoft/applicationinsights-web';
import { catchError, from, of, switchMap, tap } from 'rxjs';
import { createEffect, publishStoreEvent } from 'signalstory';
import { reportVersionReloadProblem } from '../../version-reload-diagnostics';
import { newVersionDeployed } from '../events/app-version.event';
import { AppVersionStore } from '../stores/app-version.store';

interface VersionFile {
  AmiVersion?: string;
}

/**
 * A version is letters, digits, dots, hyphens and plus signs only (e.g. 2.20.1, 2.21.0-qa-jobboss0001).
 * Anything else is treated as a bad file, not a new version: the value ends up in a toast and in
 * telemetry, so it is validated here rather than trusted from the network.
 */
const VERSION_PATTERN = /^[A-Za-z0-9.+-]{1,64}$/;

/**
 * Fetches the deployed version file (bypassing caches) and publishes newVersionDeployed when it
 * differs from the running bundle. Any failure (network, non-200, bad JSON) is reported and
 * swallowed; the next trigger simply tries again.
 */
export const checkDeployedVersionEffect = createEffect(
  'Check Deployed Version',
  (store: AppVersionStore, reason: string) => {
    const appInsights = inject(ApplicationInsights, { optional: true });
    const { running, versionUrl } = store.state();
    if (!running) return of(null);

    return from(fetch(`${versionUrl}?v=${Date.now()}`, { cache: 'no-store' })).pipe(
      switchMap((response) => {
        if (!response.ok) {
          reportVersionReloadProblem(appInsights, `Version file request returned ${response.status}`, undefined, { versionUrl, reason });
          return of(null);
        }
        return from(response.json() as Promise<VersionFile>);
      }),
      tap((body) => {
        if (body === null) return;
        const deployed = (body?.AmiVersion ?? '').trim();
        if (!deployed) {
          reportVersionReloadProblem(appInsights, 'Version file has no AmiVersion value', undefined, { versionUrl, reason });
          return;
        }
        if (!VERSION_PATTERN.test(deployed)) {
          reportVersionReloadProblem(appInsights, 'Version file value is not a valid version; ignoring it', undefined,
            { versionUrl, reason, deployed: deployed.slice(0, 64) });
          return;
        }
        if (deployed !== running) {
          publishStoreEvent(newVersionDeployed, { running, deployed, reason });
        }
      }),
      catchError((error) => {
        reportVersionReloadProblem(appInsights, 'Version check failed', error, { versionUrl, reason });
        return of(null);
      })
    );
  }
);
